<template>
  <section>
    <div v-if="items.length === 0" class="data-table-empty is-compact">暂无评论</div>
    <ol
      v-else
      ref="listRef"
      class="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <li
        v-for="(comment, index) in rootComments"
        :key="comment.id || index"
        class="px-5 py-4 transition-colors"
        :class="commentItemClass(comment)"
        :data-comment-id="comment.id || ''"
      >
        <div class="flex items-start gap-3">
          <div class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xs font-medium text-slate-600">
            <img
              v-if="avatarOf(comment)"
              :src="avatarOf(comment)"
              :alt="`${authorOf(comment)}头像`"
              class="h-full w-full object-cover"
            >
            <span v-else>{{ initials(comment) }}</span>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <p class="text-sm font-medium text-slate-800">{{ authorOf(comment) }}</p>
              <time class="text-xs text-slate-400">{{ commentTimeOf(comment) }}</time>
            </div>
            <span v-if="comment.supplementRequested" class="status-badge mt-1.5 bg-orange-50 text-orange-700">请求补充</span>
            <p class="mt-1.5 break-words whitespace-pre-wrap text-sm leading-6 text-slate-600">{{ contentOf(comment) }}</p>
            <div v-if="canReplyTo(comment)" class="mt-2 flex justify-end">
              <button
                type="button"
                class="text-xs font-medium text-blue-600 transition-colors hover:text-blue-700"
                :disabled="disabled"
                @click="startReply(comment)"
              >
                回复
              </button>
            </div>

            <div
              v-if="replyCommentsOf(comment).length"
              class="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm leading-6 text-slate-600"
            >
              <div
                v-for="reply in replyCommentsOf(comment)"
                :key="reply.id || `${comment.id || index}-reply-${contentOf(reply)}`"
                class="rounded-md px-2 py-1 transition-colors"
                :class="commentItemClass(reply)"
                :data-comment-id="reply.id || ''"
              >
                <div class="flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                  <span class="font-medium text-slate-800">{{ authorOf(reply) }}</span>
                  <template v-if="replyTargetOf(reply)">
                    <span class="text-slate-400">回复</span>
                    <span class="font-medium text-slate-800">{{ authorOf(replyTargetOf(reply)) }}</span>
                  </template>
                  <span class="text-slate-400">：</span>
                  <span class="min-w-0 flex-1 break-words whitespace-pre-wrap">{{ contentOf(reply) }}</span>
                  <span v-if="reply.supplementRequested" class="status-badge bg-orange-50 text-orange-700">请求补充</span>
                  <button
                    v-if="canReplyTo(reply)"
                    type="button"
                    class="ml-auto text-xs font-medium text-blue-600 transition-colors hover:text-blue-700"
                    :disabled="disabled"
                    @click="startReply(reply)"
                  >
                    回复
                  </button>
                </div>
                <time class="mt-0.5 block text-xs text-slate-400">{{ commentTimeOf(reply) }}</time>
              </div>
            </div>
          </div>
        </div>
      </li>
    </ol>

    <form v-if="canSubmit || canRequestSupplement" class="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" @submit.prevent="submitComment">
      <div class="space-y-3">
        <div v-if="replyingTo" class="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
          <div class="flex flex-wrap items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="text-sm font-medium text-blue-800">回复 @{{ authorOf(replyingTo) }}</div>
              <p class="mt-0.5 break-words text-xs leading-5 text-blue-700">{{ commentExcerpt(replyingTo) }}</p>
            </div>
            <button type="button" class="text-xs font-medium text-blue-700 hover:text-blue-900" @click="cancelReply">取消回复</button>
          </div>
        </div>
        <label class="block">
          <span class="form-label">{{ replyingTo ? '回复内容' : '评论' }}</span>
          <textarea
            ref="textareaRef"
            v-model.trim="content"
            class="form-textarea mt-1 w-full"
            rows="3"
            :placeholder="replyingTo ? `回复 @${authorOf(replyingTo)}` : '请输入评论内容'"
            :disabled="disabled"
          ></textarea>
        </label>
      </div>
      <div class="mt-3 flex flex-wrap justify-end gap-2">
        <button v-if="canRequestSupplement" type="button" class="btn-secondary" :disabled="disabled || !content" @click="requestSupplement">要求补充</button>
        <button v-if="canSubmit" type="submit" class="btn-secondary" :disabled="disabled || !content">{{ replyingTo ? '提交回复' : '提交评论' }}</button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useUserStore } from '@/stores/user'

const props = defineProps({
  comments: {
    type: Array,
    default: () => []
  },
  canSubmit: { type: Boolean, default: true },
  canRequestSupplement: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  focusCommentId: { type: [String, Number], default: '' }
})
const emit = defineEmits(['submit', 'request-supplement'])

const userStore = useUserStore()
const items = computed(() => Array.isArray(props.comments) ? props.comments : [])
const commentsById = computed(() => {
  const map = new Map()
  items.value.forEach((comment) => {
    const id = normalizedId(comment?.id)
    if (id) map.set(id, comment)
  })
  return map
})
const rootComments = computed(() => items.value.filter((comment) => threadRootOf(comment) === comment))
const replyCommentsByRootId = computed(() => {
  const map = new Map()
  items.value.forEach((comment) => {
    const root = threadRootOf(comment)
    if (root === comment) return
    const rootId = normalizedId(root?.id)
    if (!rootId) return
    const comments = map.get(rootId) || []
    comments.push(comment)
    map.set(rootId, comments)
  })
  return map
})
const focusedCommentId = computed(() => normalizedId(props.focusCommentId))
const currentUserId = computed(() => normalizedId(userStore.userInfo?.userId || userStore.userInfo?.id))
const content = ref('')
const replyingTo = ref(null)
const listRef = ref(null)
const textareaRef = ref(null)

watch(
  () => [focusedCommentId.value, items.value.length],
  () => scrollFocusedCommentIntoView(),
  { immediate: true }
)

watch(items, () => {
  if (!replyingTo.value) return
  const replyId = normalizedId(replyingTo.value.id)
  if (replyId && commentsById.value.has(replyId)) return
  cancelReply()
})

function authorOf(comment) {
  return comment?.authorName
    || comment?.commenterName
    || comment?.author?.displayName
    || comment?.author?.name
    || comment?.author?.username
    || (comment?.authorUserId ? `用户 ${comment.authorUserId}` : '未署名')
}

function avatarOf(comment) {
  return comment?.authorAvatarUrl
    || comment?.avatarUrl
    || comment?.author?.avatarUrl
    || comment?.authorAvatar
    || comment?.avatar
    || comment?.author?.avatar
    || ''
}

function authorUserIdOf(comment) {
  return normalizedId(comment?.authorUserId
    || comment?.commenterUserId
    || comment?.userId
    || comment?.author?.id
    || comment?.author?.userId)
}

function isOwnComment(comment) {
  const current = currentUserId.value
  const author = authorUserIdOf(comment)
  return Boolean(current && author && current === author)
}

function canReplyTo(comment) {
  return props.canSubmit && !props.disabled && !isOwnComment(comment)
}

function initials(comment) {
  const name = authorOf(comment)
  if (name === '未署名') return '?'
  return name.slice(0, 2).toUpperCase()
}

function parentCommentOf(comment) {
  const parentId = normalizedId(comment?.parentCommentId)
  return parentId ? commentsById.value.get(parentId) : null
}

function threadRootOf(comment) {
  let current = comment
  const visited = new Set()
  while (current) {
    const currentId = normalizedId(current?.id)
    if (currentId) {
      if (visited.has(currentId)) return comment
      visited.add(currentId)
    }
    const parent = parentCommentOf(current)
    if (!parent) return current
    current = parent
  }
  return comment
}

function replyTargetOf(reply) {
  return parentCommentOf(reply)
}

function replyCommentsOf(comment) {
  const rootId = normalizedId(comment?.id)
  return rootId ? replyCommentsByRootId.value.get(rootId) || [] : []
}

function contentOf(comment) {
  return comment?.content || comment?.comment || '--'
}

function commentTimeOf(comment) {
  return comment?.createTime || comment?.createdAt || comment?.commentTime || '--'
}

function commentExcerpt(comment) {
  const text = String(contentOf(comment)).trim()
  if (!text || text === '--') return '暂无内容'
  return text.length > 48 ? `${text.slice(0, 48)}...` : text
}

function commentItemClass(comment) {
  const id = normalizedId(comment?.id)
  if (!id || id !== focusedCommentId.value) return ''
  return 'bg-blue-50/70 ring-1 ring-inset ring-blue-200'
}

function startReply(comment) {
  if (!canReplyTo(comment)) return
  replyingTo.value = comment
  nextTick(() => textareaRef.value?.focus())
}

function cancelReply() {
  replyingTo.value = null
}

function submitComment() {
  if (!props.canSubmit) return
  const normalizedContent = content.value.trim()
  if (!normalizedContent) return
  emit('submit', {
    parentCommentId: replyingTo.value?.id || null,
    content: normalizedContent
  })
  content.value = ''
  cancelReply()
}

function requestSupplement() {
  if (!props.canRequestSupplement) return
  const normalizedContent = content.value.trim()
  if (!normalizedContent) return
  emit('request-supplement', {
    content: normalizedContent
  })
  content.value = ''
  cancelReply()
}

function scrollFocusedCommentIntoView() {
  const id = focusedCommentId.value
  if (!id) return
  nextTick(() => {
    const nodes = Array.from(listRef.value?.querySelectorAll('[data-comment-id]') || [])
    const target = nodes.find((node) => node.getAttribute('data-comment-id') === id)
    target?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  })
}

function normalizedId(value) {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}
</script>
